/**
 * ShareCardPreview (web): the live "hero" card shown on the Share and
 * Customize dialogs. Same composition as the native card and the OG image:
 * a 1.91:1 card with the theme (rendered art, solid or custom colour, or a
 * Pro photo with its scrim) under a quiet silhouette, the person's link
 * top-start (Tabbio favicon only for Tabbio hosts), the framed avatar or
 * placeholder beside name and headline, and a frosted call to action
 * bottom-end. The edit control (-> Customize) is app-only glass top-end.
 * Uses the same @tabbio/utils resolver as the native preview and the OG.
 */
import type { CSSProperties } from 'react';
import { ArrowUpRight, Download } from 'lucide-react';
import type { ShareCardConfig } from '@tabbio/types';
import { resolveShareCard, type ResolvedShareCard } from '@tabbio/utils/cv-share-card';
import {
  SHARE_CARD_ASPECT,
  SHARE_CARD_AVATAR_PLACEHOLDER_ART,
  SHARE_CARD_BRUSH_ICON,
  SHARE_CARD_FIXED,
  SHARE_CARD_FLUID,
  SHARE_CARD_ORBIT_ART,
  SHARE_CARD_RADIUS,
  SHARE_CARD_SILHOUETTES,
  shareCardFluidCss,
  shareCardInkAlpha,
} from '@tabbio/utils/share-card-art';
import { TABBIO_MARK_PATH } from '../common/TabbioMark';
import { useTranslation } from 'react-i18next';
import { useLocale } from '../../hooks/useLocale';
import { useCVData } from '../../lib/cv-mock-data';
import { shareThemeArtUri } from './share-theme-art';

const CTA_INK = '#171719';
const MARK_ASPECT = 24.0361 / 27.9998;
const F = SHARE_CARD_FLUID;
const X = SHARE_CARD_FIXED;

export interface ShareCardPreviewProps {
  /**
   * The CV URL this card advertises by default. Printed on the card's top line
   * as `handle.tabbio.com` unless the card carries its own link (Pro) or has
   * cleared it; without it the row is hidden.
   */
  publicUrl?: string | null;
  /** Edit (brush) control, top-end. Not rendered without it (Customize's own preview). */
  onCustomizePress?: () => void;
  /** The call to action when it reads "View CV" (or custom wording). Opens the CV. */
  onViewPress?: () => void;
  /** The call to action when it reads "Download CV". Falls back to `onViewPress`. */
  onDownloadPress?: () => void;
  /**
   * Whether the viewer may download this CV (`useCanDownloadCvTarget`). When
   * false a "Download CV" call to action renders and acts as "View CV": a
   * grantee without the export permission is never offered a download the
   * API refuses. Defaults to true (the owner's own card).
   */
  canDownload?: boolean;
  /**
   * Override the card config to render (issue #221): the Customize sheet passes
   * its live local `card` state so the preview stays in lockstep with the sheet's
   * toggles instead of lagging a beat behind `cvConfig.card` from context.
   */
  card?: ShareCardConfig | null;
}

export function ShareCardPreview({
  publicUrl,
  onCustomizePress,
  onViewPress,
  onDownloadPress,
  canDownload = true,
  card: cardOverride,
}: ShareCardPreviewProps) {
  const { profile, cvConfig } = useCVData();
  const { t } = useTranslation('seeker');
  const { isRTL } = useLocale();
  const activeCard = cardOverride !== undefined ? cardOverride : cvConfig.card;
  const resolved = resolveShareCard(activeCard, {
    title: profile.name || 'Your Name',
    headline: profile.headline,
    summary: profile.summary,
    showPhoto: true,
    publicUrl,
  });
  const ink = resolved.ink;
  const avatarUri = resolved.showPhoto && profile.avatarUrl ? profile.avatarUrl : null;
  const downloadCta = resolved.cta.kind === 'download' && canDownload;
  const ctaLabel =
    resolved.cta.kind === 'download' && !canDownload
      ? t('cv.share.cardCta')
      : resolved.cta.preset === 'view'
        ? t('cv.share.cardCta')
        : resolved.cta.preset === 'download'
          ? t('cv.share.downloadCv')
          : resolved.cta.label;
  const onCtaPress = downloadCta ? (onDownloadPress ?? onViewPress) : onViewPress;

  const fill: CSSProperties = resolved.backgroundImage
    ? {
        backgroundImage: `linear-gradient(rgba(0,0,0,0.28), rgba(0,0,0,0.28)), url(${resolved.backgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }
    : resolved.artKey
      ? {
          backgroundImage: `url(${shareThemeArtUri(resolved.artKey)})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }
      : resolved.css.includes('gradient')
        ? { backgroundImage: resolved.css }
        : { backgroundColor: resolved.css };

  const avatarSize = shareCardFluidCss(F.avatar);

  return (
    // The wrapper is the container the `cqi` units below measure against.
    <div style={{ width: '100%', containerType: 'inline-size' }}>
      <div
        style={{
          position: 'relative',
          width: '100%',
          aspectRatio: `${SHARE_CARD_ASPECT} / 1`,
          borderRadius: SHARE_CARD_RADIUS,
          overflow: 'hidden',
          color: ink,
          fontFamily: "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
          boxShadow: resolved.ring ? `inset 0 0 0 ${X.hairline}px ${resolved.ring}` : undefined,
          ...fill,
        }}
      >
        <Silhouette resolved={resolved} />

        <div
          style={{
            position: 'relative',
            zIndex: 2,
            height: '100%',
            display: 'grid',
            gridTemplateRows: `${X.linkRow}px minmax(0, 1fr) ${X.ctaMinHeight}px`,
            gap: X.rowGap,
            boxSizing: 'border-box',
            padding: shareCardFluidCss(F.padding),
          }}
        >
          {/* Top-start: the link. One soft ink for favicon and text; the row
              stops short of the edit control. */}
          <div
            style={{
              minWidth: 0,
              display: 'flex',
              alignItems: 'center',
              paddingInlineEnd: X.editHit,
            }}
          >
            {resolved.link.display ? (
              <div
                dir="ltr"
                style={{
                  minWidth: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  color: resolved.linkInk,
                  fontSize: shareCardFluidCss(F.linkFont),
                  fontWeight: 500,
                  lineHeight: 1.5,
                }}
              >
                {resolved.link.showFavicon ? (
                  <svg
                    width={X.favicon * MARK_ASPECT}
                    height={X.favicon}
                    viewBox="0 0 24.0361 27.9998"
                    aria-hidden
                    style={{ flexShrink: 0 }}
                  >
                    <path d={TABBIO_MARK_PATH} fill="currentColor" />
                  </svg>
                ) : null}
                <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {resolved.link.display}
                </span>
              </div>
            ) : null}
          </div>

          {/* Middle: avatar + name + headline, one row from the start edge */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: shareCardFluidCss(F.identityGap),
              minWidth: 0,
            }}
          >
            <div
              style={{
                position: 'relative',
                flexShrink: 0,
                width: avatarSize,
                height: avatarSize,
                borderRadius: 999,
                overflow: 'hidden',
                display: 'grid',
                placeItems: 'center',
                background: avatarUri ? 'transparent' : shareCardInkAlpha(ink, 0.08),
                boxShadow: `inset 0 0 0 ${X.hairline}px rgba(255, 255, 255, 0.4)`,
              }}
            >
              {avatarUri ? (
                <img
                  src={avatarUri}
                  alt=""
                  style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                <svg
                  width="52%"
                  height="52%"
                  viewBox={SHARE_CARD_AVATAR_PLACEHOLDER_ART.viewBox}
                  aria-hidden
                >
                  <path d={SHARE_CARD_AVATAR_PLACEHOLDER_ART.path} fill={shareCardInkAlpha(ink, 0.42)} />
                </svg>
              )}
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div
                dir="auto"
                style={{
                  fontSize: shareCardFluidCss(F.nameFont),
                  fontWeight: 650,
                  letterSpacing: X.nameLetterSpacing,
                  // Keep in sync with the native card so tall Cairo (Arabic)
                  // glyphs aren't clipped.
                  lineHeight: 1.3,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {resolved.title}
              </div>
              {resolved.description ? (
                <div
                  dir="auto"
                  style={{
                    marginTop: 3,
                    fontSize: shareCardFluidCss(F.roleFont),
                    lineHeight: 1.4,
                    opacity: 0.8,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {resolved.description}
                </div>
              ) : null}
            </div>
          </div>

          {/* Bottom-end: the frosted call to action. */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end', minWidth: 0 }}>
            <button
              type="button"
              onClick={onCtaPress}
              // Without a handler this is a picture of a button: say so rather
              // than showing a pointer cursor that does nothing (issue #263).
              disabled={!onCtaPress}
              aria-label={ctaLabel}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: X.ctaGap,
                minHeight: X.ctaMinHeight,
                maxWidth: '75%',
                padding: `0 ${X.ctaPaddingX}px`,
                borderRadius: 999,
                border: `${X.hairline}px solid rgba(255, 255, 255, 0.65)`,
                background: 'rgba(255, 255, 255, 0.78)',
                backdropFilter: 'blur(12px) saturate(120%)',
                WebkitBackdropFilter: 'blur(12px) saturate(120%)',
                boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.44), 0 2px 8px rgba(0, 0, 0, 0.07)',
                color: CTA_INK,
                fontFamily: 'inherit',
                fontSize: shareCardFluidCss(F.ctaFont),
                fontWeight: 650,
                whiteSpace: 'nowrap',
                cursor: onCtaPress ? 'pointer' : 'default',
              }}
            >
              <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{ctaLabel}</span>
              {resolved.cta.kind === 'download' ? (
                <Download size={X.ctaIcon} color={CTA_INK} strokeWidth={2} style={{ flexShrink: 0 }} />
              ) : (
                <ArrowUpRight
                  size={X.ctaIcon}
                  color={CTA_INK}
                  strokeWidth={2}
                  style={{ flexShrink: 0, transform: isRTL ? 'scaleX(-1)' : undefined }}
                />
              )}
            </button>
          </div>
        </div>

        {/* App-only: the glass edit control (-> Customize), floating top-end */}
        {onCustomizePress ? (
          <button
            type="button"
            aria-label={t('cv.customizeCard.editorTitle')}
            onClick={onCustomizePress}
            style={{
              position: 'absolute',
              top: X.editOffset,
              insetInlineEnd: X.editOffset,
              zIndex: 3,
              width: X.editHit,
              height: X.editHit,
              padding: X.editInset,
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              // The brush: the ink, or white where the theme's corner is dark.
              color: resolved.editInk,
            }}
          >
            <span
              style={{
                display: 'grid',
                placeItems: 'center',
                width: '100%',
                height: '100%',
                borderRadius: 999,
                background: shareCardInkAlpha(ink, 0.1),
                border: `${X.hairline}px solid ${shareCardInkAlpha(ink, 0.18)}`,
                boxSizing: 'border-box',
                backdropFilter: 'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)',
              }}
            >
              <svg
                width={X.editIcon}
                height={X.editIcon}
                viewBox={SHARE_CARD_BRUSH_ICON.viewBox}
                fill="none"
                aria-hidden
              >
                {SHARE_CARD_BRUSH_ICON.paths.map((d) => (
                  <path
                    key={d}
                    d={d}
                    stroke="currentColor"
                    strokeWidth={SHARE_CARD_BRUSH_ICON.strokeWidth}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                ))}
              </svg>
            </span>
          </button>
        ) : null}
      </div>
    </div>
  );
}

/** The quiet silhouette behind the content: one path, low-opacity ink. */
function Silhouette({ resolved }: { resolved: ResolvedShareCard }) {
  if (resolved.pattern === 'none' || resolved.silhouetteOpacity <= 0) return null;
  if (resolved.pattern === 'orbit') {
    const fill =
      resolved.silhouetteOpacity < 1
        ? shareCardInkAlpha(resolved.ink, resolved.silhouetteOpacity)
        : resolved.orbitColor;
    return (
      <svg
        aria-hidden
        viewBox={SHARE_CARD_ORBIT_ART.viewBox}
        preserveAspectRatio="none"
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', zIndex: 1, pointerEvents: 'none' }}
      >
        <path d={SHARE_CARD_ORBIT_ART.path} fill={fill} />
      </svg>
    );
  }
  const spec = SHARE_CARD_SILHOUETTES[resolved.pattern];
  return (
    <div
      aria-hidden
      style={{
        position: 'absolute',
        zIndex: 1,
        pointerEvents: 'none',
        // Placed from the end edge so it mirrors with the layout in RTL.
        insetInlineEnd: `${spec.right * 100}%`,
        bottom: `${spec.bottom * 100}%`,
        width: `${spec.width * 100}%`,
        height: `${spec.height * 100}%`,
        transform: `rotate(${spec.rotate}deg)`,
      }}
    >
      <svg width="100%" height="100%" viewBox={spec.art.viewBox} preserveAspectRatio="xMidYMid meet">
        <path d={spec.art.path} fill={shareCardInkAlpha(resolved.ink, resolved.silhouetteOpacity)} />
      </svg>
    </div>
  );
}
